/**
 * Carrito rápido (Igor 2026-10-06): respuesta inmediata al pulsar +/− y al quitar líneas.
 *
 * Qué hace (sin tocar los archivos de Horizon; parchea el prototipo de <cart-items-component>):
 *  - Sin debounce de 300 ms: el primer clic sale al instante; los clics siguientes se agrupan
 *    mientras hay una petición en vuelo (gana el último valor por línea).
 *  - No bloquea el carrito (Horizon ponía .cart-items-disabled { pointer-events: none } durante toda la petición).
 *  - Actualización optimista: cantidad, total de línea, total del carrito, burbuja de la cabecera,
 *    insignia del drawer, barra de progreso .mf-dsc y mensaje de ahorro. Línea con opacidad .6 hasta la respuesta.
 *  - Usa la clave de línea (id) en /cart/change en vez del número de línea: robusto si cambia el orden.
 *  - Una sola petición /cart/change con las secciones necesarias; solo se hace morph de la última respuesta.
 *  - Errores / stock: revierte re-renderizando la sección y muestra un aviso discreto en italiano.
 * Si algo falla al cargar, el carrito sigue funcionando con el comportamiento nativo de Horizon.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig, prefersReducedMotion, onAnimationEnd, startViewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

if (!window.__mfCarritoRapido) {
  window.__mfCarritoRapido = true;

  const MSG_GENERIC = 'Non è stato possibile aggiornare il carrello. Riprova.';
  const MSG_STOCK = 'Quantità non disponibile: abbiamo ripristinato il carrello.';
  const msgMax = (n) => (n === 1 ? 'Disponibile solo 1 pezzo.' : `Disponibili solo ${n} pezzi.`);

  let installed = false;

  /* ---------- estilos mínimos ---------- */
  function injectStyle() {
    if (document.getElementById('mf-cr-style')) return;
    const s = document.createElement('style');
    s.id = 'mf-cr-style';
    s.textContent =
      '.cart-items__table-row .cart-items__product-info,.cart-items__table-row .cart-items__variants-wrapper,.cart-items__table-row .cart-items__price{transition:opacity .15s ease}' +
      '.cart-items__table-row.mf-cr-loading .cart-items__product-info,.cart-items__table-row.mf-cr-loading .cart-items__variants-wrapper,.cart-items__table-row.mf-cr-loading .cart-items__price{opacity:.6}' +
      'cart-items-component.cart-items-disabled{pointer-events:auto}';
    document.head.appendChild(s);
  }

  /* ---------- dinero: parsear y formatear igual que el texto que ya muestra la tienda ---------- */
  const NUM_RE = /\d[\d.,  ' ]*\d|\d/;

  function parseMoney(str) {
    const m = NUM_RE.exec(str || '');
    if (!m) return null;
    const tok = m[0].replace(/[  ' ]/g, '');
    const i = Math.max(tok.lastIndexOf(','), tok.lastIndexOf('.'));
    let intPart = tok;
    let dec = '00';
    if (i > -1 && tok.length - i - 1 === 2) {
      intPart = tok.slice(0, i);
      dec = tok.slice(i + 1);
    }
    const whole = parseInt(intPart.replace(/[.,]/g, ''), 10);
    if (isNaN(whole)) return null;
    return whole * 100 + parseInt(dec, 10);
  }

  function formatLike(sample, cents) {
    const m = NUM_RE.exec(sample || '');
    if (!m) return null;
    const tok = m[0];
    const i = Math.max(tok.lastIndexOf(','), tok.lastIndexOf('.'));
    const hasDec = i > -1 && tok.length - i - 1 === 2;
    const decChar = hasDec ? tok[i] : '';
    const intTok = hasDec ? tok.slice(0, i) : tok;
    const th = (/[.,  ' ]/.exec(intTok) || [''])[0];
    const abs = Math.abs(Math.round(cents));
    let whole = String(Math.floor(abs / 100));
    const frac = String(abs % 100).padStart(2, '0');
    if (th) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, th);
    const out = (cents < 0 ? '-' : '') + whole + (hasDec ? decChar + frac : '');
    return sample.slice(0, m.index) + out + sample.slice(m.index + tok.length);
  }

  function setMoney(el, cents) {
    const sample = (el.textContent || '').trim() || el.getAttribute('value') || '';
    const text = formatLike(sample, cents);
    if (text == null) return;
    el.textContent = text;
    if (el.hasAttribute('value')) el.setAttribute('value', text);
  }

  /* ---------- estado por <cart-items-component> ---------- */
  const states = new WeakMap();

  function stateOf(comp) {
    let st = states.get(comp);
    if (!st) {
      st = { desired: new Map(), running: false, shown: new Map(), unit: new Map(), timers: new Map() };
      states.set(comp, st);
    }
    return st;
  }

  const rowsOf = (comp) => Array.from(comp.querySelectorAll('tbody tr[data-key]'));
  const rowByKey = (comp, key) => rowsOf(comp).find((r) => r.dataset.key === key) || null;
  const inputOf = (row) => (row ? row.querySelector('input[data-cart-line]') : null);

  /** Cantidad confirmada por el servidor (atributo value, no la propiedad que ya cambió el usuario). */
  function baselineQty(row) {
    const input = inputOf(row);
    if (!input) return null;
    const v = parseInt(input.getAttribute('value'), 10);
    return isNaN(v) ? parseInt(input.value, 10) || 0 : v;
  }

  function shownQty(comp, st, key) {
    if (st.shown.has(key)) return st.shown.get(key);
    const row = rowByKey(comp, key);
    if (!row) return 0;
    const input = inputOf(row);
    const v = input ? parseInt(input.value, 10) : NaN;
    return isNaN(v) ? 0 : v;
  }

  /* ---------- actualización optimista ---------- */
  function bumpCounts(comp, dq) {
    if (!dq) return;
    document.querySelectorAll('cart-icon').forEach((icon) => {
      try {
        const c = icon.currentCartCount;
        if (Number.isFinite(c) && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(Math.max(0, c + dq), false);
      } catch (e) {
        /* no-op */
      }
    });
    const bump = (el) => {
      const c = parseInt(el.textContent, 10);
      if (!isNaN(c)) el.textContent = String(Math.max(0, c + dq));
    };
    document.querySelectorAll('#cart-drawer .theme-drawer__badge').forEach(bump);
    comp.querySelectorAll('[ref="cartItemCount"]').forEach(bump);
  }

  /** Mismos textos que snippets/progresso-sconto-carrito-claude.liquid: [versión larga, versión corta]. */
  function progressTexts(passed, n, qtys, pcts, elig) {
    const rem = passed < n ? qtys[passed] - elig : 0;
    const r = rem === 1 ? '1 articolo' : `${rem} articoli`;
    if (passed === 0) {
      const t = `Aggiungi ${r} e ottieni il <b class="mf-pct">${pcts[0]}%</b>`;
      return [t, t];
    }
    if (passed < n) {
      return [`Hai il <b class="mf-pct">${pcts[passed - 1]}%</b> di sconto · Con ${r} in più sale al <b class="mf-pct">${pcts[passed]}%</b>`, `<b class="mf-pct">${pcts[passed - 1]}%</b> sbloccato · +${r} per il <b class="mf-pct">${pcts[passed]}%</b>`];
    }
    const t = `Hai il <b class="mf-pct">${pcts[n - 1]}%</b> di sconto · Il massimo!`;
    return [t, t];
  }

  function updateProgress(comp, st) {
    const root = comp.querySelector('.mf-dsc[data-mf-eligible-keys]');
    if (!root) return;
    const keys = (root.dataset.mfEligibleKeys || '').split(',').filter(Boolean);
    const qtys = (root.dataset.mfQtys || '').split(',').map(Number).filter((n) => n > 0);
    const pcts = (root.dataset.mfPcts || '').split(',');
    const partial = parseFloat(root.dataset.mfPartial);
    const n = qtys.length;
    if (!n || pcts.length < n) return;
    let elig = 0;
    for (const k of keys) elig += shownQty(comp, st, k);
    const passed = qtys.filter((q) => elig >= q).length;
    const fill = elig < 1 ? 0 : passed >= n ? 100 : Math.round(((passed + (isNaN(partial) ? 0.75 : partial)) / n) * 10000) / 100;

    const fillEl = root.querySelector('.mf-dsc__fill');
    if (fillEl) fillEl.style.width = fill + '%';
    const bar = root.querySelector('.mf-dsc__bar');
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(fill)));

    let nextDone = false;
    root.querySelectorAll('.mf-dsc__step').forEach((step, i) => {
      const on = elig >= (qtys[i] ?? Infinity);
      step.classList.toggle('is-on', on);
      const isNext = !on && !nextDone;
      if (isNext) nextDone = true;
      step.classList.toggle('is-next', isNext);
    });

    const msg = root.querySelector('.mf-dsc__msg');
    const txt = msg && msg.querySelector('.mf-dsc__txt');
    if (msg && txt) {
      const [long, short] = progressTexts(passed, n, qtys, pcts, elig);
      const sl = document.createElement('span');
      sl.className = 'mf-dsc__long';
      sl.innerHTML = long;
      const ss = document.createElement('span');
      ss.className = 'mf-dsc__short';
      ss.innerHTML = short;
      txt.replaceChildren(sl, ss);
      msg.classList.toggle('is-done', passed >= n);
      msg.style.display = '';
    }

    /* la etiqueta verde de ahorro se recalcula con la respuesta del servidor: mientras tanto, atenuada */
    const saved = root.querySelector('.mf-dsc__saved');
    if (saved) saved.classList.add('is-stale');
  }

  /**
   * Precio unitario "N × €unit" de TODAS las líneas (con el % del tramo: 2→10, 3→15, 4→20 sobre las líneas elegibles) y
   * tarjeta "Sconti" del resumen. Optimista: lo corrige la respuesta del servidor (data-hydration-key en .mf-cart-unit y .mf-sconti).
   * Líneas elegibles = data-mf-eligible-keys de .mf-dsc; el resto conserva el unitario del servidor (data-unit).
   */
  function updateDiscountViews(comp, st) {
    const root = comp.querySelector('.mf-dsc[data-mf-eligible-keys]');
    const keys = new Set(root ? (root.dataset.mfEligibleKeys || '').split(',').filter(Boolean) : []);
    const qtys = root ? (root.dataset.mfQtys || '').split(',').map(Number).filter((n) => n > 0) : [];
    const pcts = root ? (root.dataset.mfPcts || '').split(',').map(Number) : [];
    let elig = 0;
    for (const k of keys) elig += shownQty(comp, st, k);
    const passed = qtys.filter((q) => elig >= q).length;
    const pct = passed > 0 ? pcts[passed - 1] || 0 : 0;
    let saving = 0;
    let totalSaved = 0;
    let sample = '';

    rowsOf(comp).forEach((row) => {
      const el = row.querySelector('.mf-cart-unit');
      if (!el) return;
      const key = row.dataset.key;
      const q = shownQty(comp, st, key);
      if (!(q > 0)) return;
      const orig = parseInt(el.dataset.orig, 10);
      let unit = parseInt(el.dataset.unit, 10);
      const eligible = root && keys.has(key) && !isNaN(orig);
      if (eligible) {
        /* Shopify redondea el descuento por unidad (45,95 -10% = 41,36; x2 = 82,72) */
        unit = Math.round((orig * (100 - pct)) / 100);
        saving += (orig - unit) * q;
      }
      /* "Totale risparmiato": tachado (o precio sin descuento) menos lo que se paga, en toda la compra */
      const wasEl = row.querySelector('.mf-cart-was');
      const lineEl = row.querySelector('.cart-items__price text-component');
      const wasLine = wasEl ? parseMoney(wasEl.textContent) : !isNaN(orig) ? orig * q : null;
      const finalLine = eligible ? unit * q : lineEl ? parseMoney(lineEl.textContent) : null;
      if (wasLine != null && finalLine != null && wasLine > finalLine) totalSaved += wasLine - finalLine;
      const qEl = el.querySelector('[data-mf-unit-qty]');
      if (qEl) qEl.textContent = String(q);
      const pEl = el.querySelector('[data-mf-unit-price]');
      if (pEl && !isNaN(unit)) {
        if (!sample) sample = (pEl.textContent || '').trim();
        setMoney(pEl, unit);
      }
      el.hidden = !(q >= 2 || (!isNaN(orig) && !isNaN(unit) && unit < orig));
    });

    const card = root && comp.querySelector('[data-mf-sconti]');
    if (card) {
      const show = totalSaved > 0;
      card.hidden = !show;
      if (show) {
        const label = card.querySelector('[data-mf-sconti-label]');
        const save = card.querySelector('[data-mf-sconti-save]');
        const money = formatLike(sample, totalSaved);
        if (label) label.textContent = 'Totale risparmiato:';
        if (save && money != null) save.textContent = money;
      }
    }
  }

  /** @returns {boolean} true si hay algo que enviar (la cantidad cambia respecto a lo mostrado) */
  function applyOptimistic(comp, st, key, newQty) {
    const row = rowByKey(comp, key);
    const prevQty = st.shown.has(key) ? st.shown.get(key) : row ? baselineQty(row) : null;
    if (prevQty == null) return true;
    if (prevQty === newQty) return false;

    try {
      st.shown.set(key, newQty);
      if (row) row.classList.add('mf-cr-loading');

      const lineEl = row && row.querySelector('.cart-items__price text-component');
      if (lineEl) {
        let unit = st.unit.get(key);
        const curTot = parseMoney(lineEl.textContent);
        if (unit === undefined) {
          unit = curTot != null && prevQty > 0 ? curTot / prevQty : null;
          st.unit.set(key, unit);
        }
        if (unit != null && curTot != null) {
          const newTot = newQty > 0 ? Math.round(unit * newQty) : 0;
          if (newQty > 0) setMoney(lineEl, newTot);
          const was = row.querySelector('.mf-cart-was');
          if (was && prevQty > 0 && newQty > 0) {
            const w = parseMoney(was.textContent);
            if (w != null) setMoney(was, Math.round((w * newQty) / prevQty));
          }
          const delta = newTot - curTot;
          comp.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
            const c = parseMoney(el.textContent);
            if (c != null) setMoney(el, c + delta);
          });
        }
      }

      bumpCounts(comp, newQty - prevQty);
      updateProgress(comp, st);
      updateDiscountViews(comp, st);
    } catch (e) {
      /* la actualización optimista nunca debe impedir la petición real */
    }
    return true;
  }

  /* ---------- mensajes ---------- */
  function showRowMessage(comp, key, text) {
    const row = rowByKey(comp, key);
    if (!row) return;
    const box = row.querySelector('.cart-items__error');
    const small = row.querySelector('.cart-item__error-text');
    if (!box || !small) return;
    small.textContent = text;
    box.classList.remove('hidden');
    const st = stateOf(comp);
    clearTimeout(st.timers.get(key));
    st.timers.set(
      key,
      setTimeout(() => {
        if (small.textContent === text) box.classList.add('hidden');
      }, 4500)
    );
  }

  /* ---------- peticiones ---------- */
  function sectionIds(comp) {
    const ids = new Set();
    if (comp.dataset.sectionId) ids.add(comp.dataset.sectionId);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  const modeOf = (comp) => (comp.dataset.drawer !== undefined ? 'hydration' : 'full');

  function syncSelectors(json) {
    if (!json || !json.items) return;
    for (const item of json.items) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${item.variant_id}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', String(item.quantity));
        if (typeof selector.updateCartQuantity === 'function') selector.updateCartQuantity();
      }
    }
    document.querySelectorAll('cart-quantity-selector-component').forEach((s) => s.updateButtonStates && s.updateButtonStates());
  }

  function clearLoading(comp) {
    comp.querySelectorAll('.mf-cr-loading').forEach((r) => r.classList.remove('mf-cr-loading'));
  }

  function enqueue(comp, key, qty) {
    const st = stateOf(comp);
    const changed = applyOptimistic(comp, st, key, qty);
    if (!changed && !st.desired.has(key)) return;
    st.desired.set(key, qty);
    if (!st.running) run(comp, st);
  }

  async function run(comp, st) {
    st.running = true;
    let deferred;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
    } catch (e) {
      deferred = { promise: Promise.resolve(), resolve() {}, reject() {} };
    }
    deferred.promise.catch(() => {});

    const [firstKey, firstQty] = st.desired.entries().next().value;
    try {
      comp.dispatchEvent(
        new CartLinesUpdateEvent({
          action: firstQty > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: firstKey, quantity: firstQty }],
          promise: deferred.promise,
        })
      );
    } catch (e) {
      /* no-op */
    }

    let json = null;
    let batch = [];
    let failure = null;

    try {
      while (st.desired.size) {
        batch = Array.from(st.desired.entries());
        st.desired.clear();
        for (let i = 0; i < batch.length; i++) {
          const [key, qty] = batch[i];
          const body = { id: key, quantity: qty };
          if (i === batch.length - 1) {
            body.sections = sectionIds(comp).join(',');
            body.sections_url = window.location.pathname;
          }
          const res = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(body) }));
          let data = null;
          try {
            data = await res.json();
          } catch (e) {
            /* no-op */
          }
          if (!res.ok || !data || data.errors || (data.status && data.status >= 400)) {
            failure = { key, status: res.status, data };
            throw new Error('cart-change-failed');
          }
          json = data;
        }
      }
    } catch (error) {
      st.running = false;
      st.desired.clear();
      st.shown.clear();
      st.unit.clear();
      const message = failure ? (failure.status === 422 ? MSG_STOCK : MSG_GENERIC) : MSG_GENERIC;
      deferred.reject(error);
      try {
        comp.dispatchEvent(new CartErrorEvent({ error: message, code: failure ? 'INVALID' : 'SERVICE_UNAVAILABLE' }));
      } catch (e) {
        /* no-op */
      }
      try {
        await sectionRenderer.renderSection(comp.dataset.sectionId, { cache: false, mode: modeOf(comp) });
      } catch (e) {
        /* no-op */
      }
      clearLoading(comp);
      syncSelectors(null);
      if (failure) showRowMessage(comp, failure.key, message);
      return;
    }

    /* éxito: ya no queda nada pendiente (mismo tick que la salida del bucle) */
    st.running = false;
    st.shown.clear();
    st.unit.clear();

    let cart;
    try {
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(json);
    } catch (e) {
      cart = undefined;
    }
    deferred.resolve({
      cart,
      detail: {
        sections: json.sections,
        items: json.items,
        itemCount: json.item_count,
        source: 'cart-items-component',
        didError: false,
      },
    });

    syncSelectors(json);

    const sectionId = comp.dataset.sectionId;
    const html = json.sections && json.sections[sectionId];
    const rerender = () => sectionRenderer.renderSection(sectionId, { cache: false, mode: modeOf(comp) }).catch(() => {});
    try {
      if (html) {
        Promise.resolve(morphSection(sectionId, html, { mode: modeOf(comp) })).catch(rerender);
      } else {
        rerender();
      }
    } catch (e) {
      rerender();
    }
    clearLoading(comp);
    document.querySelectorAll('cart-quantity-selector-component').forEach((s) => s.updateButtonStates && s.updateButtonStates());

    /* ¿el servidor ajustó la cantidad (stock)? */
    for (const [key, qty] of batch) {
      if (qty <= 0) continue;
      const item = (json.items || []).find((it) => it.key === key);
      if (item && item.quantity < qty) showRowMessage(comp, key, msgMax(item.quantity));
    }
  }

  /* ---------- parche de <cart-items-component> ---------- */
  async function install() {
    await customElements.whenDefined('cart-items-component');
    const proto = customElements.get('cart-items-component').prototype;
    if (proto.__mfCarritoRapido) return;
    proto.__mfCarritoRapido = true;
    injectStyle();

    /** Sustituye al updateQuantity nativo (con 'line' por posición): ahora por clave de línea y sin bloqueo. */
    proto.updateQuantity = function (config) {
      const rows = (this.refs && this.refs.cartItemRows) || rowsOf(this);
      const row = rows[config.line - 1];
      if (!row || !row.dataset.key) return;
      enqueue(this, row.dataset.key, config.quantity);
    };

    /** Papelera: localiza la fila por el clic (no por un número de línea que puede haber quedado viejo). */
    proto.onLineItemRemove = function (line, event) {
      const rows = (this.refs && this.refs.cartItemRows) || rowsOf(this);
      const fromEvent = event && event.target && event.target.closest ? event.target.closest('tr[data-key]') : null;
      const row = fromEvent || rows[line - 1];
      if (!row || !row.dataset.key) return;
      const key = row.dataset.key;

      const live = rowsOf(this).filter((r) => !r.classList.contains('removing'));
      const toRemove = [row, ...live.filter((r) => r !== row && r.dataset.parentKey === key)];
      const isEmptyCart = toRemove.length === live.length;

      enqueue(this, key, 0);

      const template = document.getElementById('empty-cart-template');
      if (isEmptyCart && template instanceof HTMLTemplateElement) {
        const clone = document.importNode(template.content, true);
        startViewTransition(() => {
          document.getElementById('cart-drawer-heading')?.remove();
          this.replaceChildren(clone);
        }, [this.dataset.drawer !== undefined ? 'empty-cart-drawer' : 'empty-cart-page']);
        return;
      }

      toRemove.forEach((r) => {
        const remove = () => r.remove();
        if (prefersReducedMotion()) return remove();
        r.style.setProperty('--row-height', `${r.clientHeight}px`);
        r.classList.add('removing');
        onAnimationEnd(r, remove);
      });
    };

    installed = true;
  }

  /* ---------- cambio de cantidad (+/−/teclado): adelantarse al debounce nativo ---------- */
  window.addEventListener(
    ThemeEvents.quantitySelectorUpdate,
    (event) => {
      if (!installed) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const comp = target.closest('cart-items-component');
      if (!comp) return;
      const detail = event.detail || {};
      if (!detail.cartLine || !(detail.quantity > 0)) return; // quantity 0 → flujo nativo (papelera)
      const row = target.closest('tr[data-key]');
      if (!row || !row.dataset.key) return;
      event.stopPropagation();
      enqueue(comp, row.dataset.key, detail.quantity);
    },
    true
  );

  install().catch((e) => console.warn('[carrito-rapido] no instalado, se usa el carrito nativo', e));
}
